import { animate } from 'motion/mini';

type AnimationPreset = {
	keyframes: Parameters<typeof animate>[1];
	options?: Record<string, unknown>;
};

/** Expo-like ease-out: fast start, long gentle settle. */
const DEFAULT_EASING = [0.16, 1, 0.3, 1] as const;
const DEFAULT_DURATION = 0.8;
const OFFSET = 24;

const PRESETS: Record<string, AnimationPreset> = {
	// Entrances
	'blur-in-up': {
		keyframes: {
			filter: ['blur(6px)', 'blur(0px)'],
			opacity: [0, 1],
			transform: [`translateY(${OFFSET - 8}px)`, 'translateY(0)'],
		},
		options: { duration: 0.9, ease: DEFAULT_EASING },
	},
	// Loops
	breath: {
		keyframes: { scale: [1, 1.05, 1] },
		options: { duration: 3, ease: 'easeInOut', repeat: Infinity },
	},
	'down-and-up': {
		keyframes: {
			transform: ['translateY(0)', 'translateY(10px)', 'translateY(0)'],
		},
		options: { duration: 4, ease: 'easeInOut', repeat: Infinity },
	},
	'fade-in': {
		keyframes: { opacity: [0, 1] },
		options: { ease: 'easeOut' },
	},

	'fade-in-down': {
		keyframes: {
			opacity: [0, 1],
			transform: [`translateY(-${OFFSET}px)`, 'translateY(0)'],
		},
		options: { ease: DEFAULT_EASING },
	},
	'fade-in-left': {
		keyframes: {
			opacity: [0, 1],
			transform: [`translateX(${OFFSET}px)`, 'translateX(0)'],
		},
		options: { ease: DEFAULT_EASING },
	},
	'fade-in-right': {
		keyframes: {
			opacity: [0, 1],
			transform: [`translateX(-${OFFSET}px)`, 'translateX(0)'],
		},
		options: { ease: DEFAULT_EASING },
	},
	'fade-in-up': {
		keyframes: {
			opacity: [0, 1],
			transform: [`translateY(${OFFSET}px)`, 'translateY(0)'],
		},
		options: { ease: DEFAULT_EASING },
	},
	'line-x': {
		keyframes: { opacity: [1, 1], transform: ['scaleX(0)', 'scaleX(1)'] },
		options: { duration: 1.1, ease: DEFAULT_EASING },
	},
	'line-y': {
		keyframes: { opacity: [1, 1], transform: ['scaleY(0)', 'scaleY(1)'] },
		options: { duration: 1.2, ease: DEFAULT_EASING },
	},
	'reveal-up': {
		keyframes: {
			clipPath: ['inset(100% 0% 0% 0%)', 'inset(0% 0% 0% 0%)'],
			opacity: [1, 1],
		},
		options: { duration: 1.2, ease: [0.77, 0, 0.175, 1] },
	},
	'scale-up': {
		keyframes: { opacity: [0, 1], scale: [0.97, 1] },
		options: { ease: DEFAULT_EASING },
	},
	'scroll-cue': {
		keyframes: {
			opacity: [1, 0.35, 1],
			transform: ['scaleY(1)', 'scaleY(0.6)', 'scaleY(1)'],
		},
		options: { duration: 2.4, ease: 'easeInOut', repeat: Infinity },
	},
	'up-and-down': {
		keyframes: {
			transform: ['translateY(0)', 'translateY(-10px)', 'translateY(0)'],
		},
		options: { duration: 4, ease: 'easeInOut', repeat: Infinity },
	},
	'zoom-out': {
		keyframes: { opacity: [0, 1], scale: [1.08, 1] },
		options: { duration: 1.6, ease: DEFAULT_EASING },
	},
};

const prefersReducedMotion = () =>
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Common animation runner
 */
const runAnimation = (
	element: HTMLElement,
	type: string,
	baseOptions: { delay?: number; duration?: number },
) => {
	const preset = PRESETS[type] || PRESETS['fade-in-up'];

	// Preset defaults (e.g. duration) must not be overridden by undefined values
	const options = {
		duration: DEFAULT_DURATION,
		...preset.options,
		...baseOptions,
	};

	return animate(
		element,
		preset.keyframes,
		options as Parameters<typeof animate>[2],
	);
};

/**
 * Lightweight parallax engine
 */
const initParallax = () => {
	const run = () => {
		const elements = document.querySelectorAll('[data-parallax]');
		if (elements.length === 0) return;

		let ticking = false;

		const updateParallax = () => {
			const viewportHeight = window.innerHeight;

			elements.forEach((el) => {
				const element = el as HTMLElement;
				const speedY = parseFloat(
					element.getAttribute('data-parallax-y') ||
						element.getAttribute('data-parallax-speed') ||
						'0.1',
				);
				const speedX = parseFloat(
					element.getAttribute('data-parallax-x') || '0',
				);
				const rect = element.getBoundingClientRect();

				// Only calculate if element is visible or near visible
				if (rect.top < viewportHeight && rect.bottom > 0) {
					// Calculate distance from center of viewport
					const elementCenter = rect.top + rect.height / 2;
					const viewportCenter = viewportHeight / 2;
					const distanceFromCenter = elementCenter - viewportCenter;

					// Apply movement
					if (speedY !== 0) {
						element.style.setProperty(
							'--parallax-y',
							`${distanceFromCenter * speedY}px`,
						);
					}
					if (speedX !== 0) {
						element.style.setProperty(
							'--parallax-x',
							`${distanceFromCenter * speedX}px`,
						);
					}

					if (!element.classList.contains('parallax-ready')) {
						element.classList.add('parallax-ready');
					}
				}
			});

			ticking = false;
		};

		const onScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(updateParallax);
				ticking = true;
			}
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
		updateParallax(); // Initial run
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', run);
	} else {
		run();
	}
};

/**
 * Initialize animations for elements with data-animate attribute
 */
export const initAnimations = () => {
	// Reduced motion: CSS already reveals everything. Skip engine entirely.
	if (prefersReducedMotion()) return;

	initParallax();

	const observerOptions = {
		// Trigger slightly inside the viewport, but small enough that
		// elements near the very bottom of the page can still intersect.
		rootMargin: '0px 0px -32px 0px',
		threshold: 0.1,
	};

	const play = (
		element: HTMLElement,
		type: string | undefined,
		loopType: string | undefined,
		baseOptions: { delay?: number; duration?: number },
	) => {
		if (type) {
			const animation = runAnimation(element, type, baseOptions);
			if (loopType) {
				animation.finished.then(() => {
					runAnimation(element, loopType, {});
				});
			}
		} else if (loopType) {
			runAnimation(element, loopType, baseOptions);
		}
	};

	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (!entry.isIntersecting) return;
			const element = entry.target as HTMLElement;
			const delay = parseFloat(element.dataset.animateDelay || '0');
			const durationAttr = element.dataset.animateDuration;

			const baseOptions: { delay?: number; duration?: number } = { delay };
			if (durationAttr) baseOptions.duration = parseFloat(durationAttr);

			play(
				element,
				element.dataset.animate,
				element.dataset.animateLoop,
				baseOptions,
			);
			observer.unobserve(element);
		});
	}, observerOptions);

	// Observe single elements
	// (children of stagger containers are driven by their container instead)
	document
		.querySelectorAll<HTMLElement>('[data-animate], [data-animate-loop]')
		.forEach((el) => {
			const parent = el.parentElement;
			if (parent?.hasAttribute('data-animate-stagger')) return;
			observer.observe(el);
		});

	// Observe staggered containers
	document.querySelectorAll('[data-animate-stagger]').forEach((container) => {
		const element = container as HTMLElement;
		const selector = element.dataset.animateStagger || ':scope > *';
		const staggerDelay = parseFloat(
			element.dataset.animateStaggerDelay || '0.1',
		);
		const baseDelay = parseFloat(element.dataset.animateDelay || '0');
		const durationAttr = element.dataset.animateDuration;
		const children = element.querySelectorAll<HTMLElement>(selector);

		const containerObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				children.forEach((childElement, index) => {
					const type =
						childElement.dataset.animate ||
						element.dataset.animateChild ||
						'fade-in-up';
					const loopType =
						childElement.dataset.animateLoop ||
						element.dataset.animateLoopChild;

					const baseOptions: { delay?: number; duration?: number } = {
						delay: baseDelay + index * staggerDelay,
					};
					if (durationAttr) baseOptions.duration = parseFloat(durationAttr);

					play(childElement, type, loopType, baseOptions);
				});
				containerObserver.unobserve(element);
			});
		}, observerOptions);

		containerObserver.observe(element);
	});
};
